import React, { useState, useRef, useCallback, useEffect } from 'react';
import { authenticatedApiCall } from '../../../utils/apiClient';
import { normalizeRegionsForEditor } from './formatBuilderUtils';

const REGION_COLORS = ['#0d6efd', '#198754', '#dc3545', '#ffc107', '#6f42c1', '#0dcaf0', '#fd7e14', '#d63384'];
const REGION_TYPES = [
  { value: 'table', label: 'Table (BOM, Parts List)' },
  { value: 'metadata', label: 'Metadata (Title Block, Info)' },
];

const RegionEditor = ({ formatId, regions: initialRegions, onSave, readOnly = false, instance, accounts, pdfToImageEndpoint }) => {
  const [regions, setRegions] = useState(() => normalizeRegionsForEditor(initialRegions || []));
  const [pdfImage, setPdfImage] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [drawing, setDrawing] = useState(false);
  const [drawStart, setDrawStart] = useState(null);
  const [dragging, setDragging] = useState(null);
  const [resizing, setResizing] = useState(null);
  const [dirty, setDirty] = useState(false);

  const containerRef = useRef(null);
  const imgRef = useRef(null);
  const fileInputRef = useRef(null);

  const getImgRect = useCallback(() => {
    if (!imgRef.current) return null;
    return imgRef.current.getBoundingClientRect();
  }, []);

  const handleUploadPdf = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const endpoint = pdfToImageEndpoint || '/pdf-extractor/builder/pdf-to-image';
      const res = await authenticatedApiCall(instance, accounts, 'POST', endpoint, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (res.data.success) {
        setPdfImage(res.data.data.image);
      }
    } catch (err) {
      alert('Failed to convert PDF to image');
      console.error(err);
    } finally {
      setUploading(false);
    }
  };

  const handleMouseDown = useCallback((e) => {
    if (!pdfImage || readOnly) return;
    const rect = getImgRect();
    if (!rect) return;
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    if (x < 0 || x > 1 || y < 0 || y > 1) return;

    // Check if clicking on a resize handle
    for (let i = regions.length - 1; i >= 0; i--) {
      const r = regions[i];
      const rRight = (r.left_percent || 0) + (r.width_percent || 0);
      const rBottom = (r.top_percent || 0) + (r.height_percent || 0);
      const handleSize = 10 / rect.width;
      if (Math.abs(x - rRight) < handleSize && Math.abs(y - rBottom) < handleSize) {
        e.preventDefault();
        setResizing({ index: i, startX: x, startY: y });
        setSelectedRegion(i);
        return;
      }
    }

    // Check if clicking inside an existing region to drag
    for (let i = regions.length - 1; i >= 0; i--) {
      const r = regions[i];
      if (x >= (r.left_percent || 0) && x <= (r.left_percent || 0) + (r.width_percent || 0) &&
          y >= (r.top_percent || 0) && y <= (r.top_percent || 0) + (r.height_percent || 0)) {
        e.preventDefault();
        setDragging({ index: i, offsetX: x - (r.left_percent || 0), offsetY: y - (r.top_percent || 0) });
        setSelectedRegion(i);
        return;
      }
    }

    // Start drawing new region
    e.preventDefault();
    setDrawing(true);
    setDrawStart({ x, y });
    setSelectedRegion(null);
  }, [pdfImage, regions, getImgRect, readOnly]);

  const handleMouseMove = useCallback((e) => {
    if (readOnly) return;
    const rect = getImgRect();
    if (!rect) return;
    const x = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const y = Math.max(0, Math.min(1, (e.clientY - rect.top) / rect.height));

    if (resizing) {
      e.preventDefault();
      setRegions(prev => {
        const updated = [...prev];
        const r = { ...updated[resizing.index] };
        r.width_percent = Math.max(0.02, x - (r.left_percent || 0));
        r.height_percent = Math.max(0.02, y - (r.top_percent || 0));
        updated[resizing.index] = r;
        return updated;
      });
      setDirty(true);
    } else if (dragging) {
      e.preventDefault();
      setRegions(prev => {
        const updated = [...prev];
        const r = { ...updated[dragging.index] };
        r.left_percent = Math.max(0, Math.min(1 - (r.width_percent || 0), x - dragging.offsetX));
        r.top_percent = Math.max(0, Math.min(1 - (r.height_percent || 0), y - dragging.offsetY));
        updated[dragging.index] = r;
        return updated;
      });
      setDirty(true);
    }
  }, [resizing, dragging, getImgRect, readOnly]);

  const handleMouseUp = useCallback((e) => {
    if (readOnly) { setResizing(null); setDragging(null); setDrawing(false); setDrawStart(null); return; }
    if (resizing) { setResizing(null); return; }
    if (dragging) { setDragging(null); return; }
    if (drawing && drawStart) {
      const rect = getImgRect();
      if (!rect) { setDrawing(false); setDrawStart(null); return; }
      const x = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
      const y = Math.max(0, Math.min(1, (e.clientY - rect.top) / rect.height));
      const left = Math.min(drawStart.x, x);
      const top = Math.min(drawStart.y, y);
      const w = Math.abs(x - drawStart.x);
      const h = Math.abs(y - drawStart.y);
      if (w > 0.01 && h > 0.01) {
        const nextId = regions.reduce((max, r) => Math.max(max, Number(r.id) || 0), 0) + 1;
        const newRegion = {
          id: nextId,
          region_name: `region_${regions.length + 1}`,
          region_label: `Region ${regions.length + 1}`,
          type: 'table',
          left_percent: left,
          top_percent: top,
          width_percent: w,
          height_percent: h,
          sort_order: regions.length,
        };
        setRegions(prev => [...prev, newRegion]);
        setSelectedRegion(regions.length);
        setDirty(true);
      }
      setDrawing(false);
      setDrawStart(null);
    }
  }, [drawing, drawStart, regions, resizing, dragging, getImgRect, readOnly]);

  useEffect(() => {
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => { window.removeEventListener('mousemove', handleMouseMove); window.removeEventListener('mouseup', handleMouseUp); };
  }, [handleMouseMove, handleMouseUp]);

  const handleRegionPropChange = (index, key, value) => {
    setRegions(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [key]: value };
      return updated;
    });
    setDirty(true);
  };

  const handleRemoveRegion = (index) => {
    setRegions(prev => prev.filter((_, i) => i !== index));
    if (selectedRegion === index) setSelectedRegion(null);
    else if (selectedRegion > index) setSelectedRegion(selectedRegion - 1);
    setDirty(true);
  };

  const handleSaveClick = async () => {
    if (readOnly || !onSave) return;
    const result = await onSave(regions);
    if (result) {
      setRegions(normalizeRegionsForEditor(result));
      setDirty(false);
    }
  };

  return (
    <div>
      {readOnly && (
        <div style={{ marginBottom: 12, padding: '8px 12px', backgroundColor: '#fff3cd', border: '1px solid #ffe69c', borderRadius: 6, fontSize: 13, color: '#664d03' }}>
          Regions are read-only for this format.
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <input ref={fileInputRef} type="file" accept=".pdf" onChange={handleUploadPdf} style={{ display: 'none' }} />
          <button onClick={() => fileInputRef.current?.click()} disabled={uploading} style={btnOutline}>
            {uploading ? 'Converting...' : pdfImage ? 'Change PDF Preview' : 'Upload PDF for Preview'}
          </button>
        </div>
        <button onClick={handleSaveClick} disabled={readOnly || !dirty} style={{ ...btnPrimary, opacity: (!readOnly && dirty) ? 1 : 0.5 }}>
          Save Regions
        </button>
      </div>

      <div style={{ display: 'flex', gap: 16 }}>
        {/* Image area */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {pdfImage ? (
            <div
              ref={containerRef}
              style={{ position: 'relative', cursor: drawing ? 'crosshair' : 'default', userSelect: 'none', border: '1px solid #dee2e6', borderRadius: 4 }}
              onMouseDown={handleMouseDown}
            >
              <img ref={imgRef} src={pdfImage} alt="PDF Preview" style={{ width: '100%', display: 'block' }} draggable={false} />
              {regions.map((r, i) => {
                const color = REGION_COLORS[i % REGION_COLORS.length];
                return (
                  <div
                    key={i}
                    style={{
                      position: 'absolute',
                      left: `${(r.left_percent || 0) * 100}%`,
                      top: `${(r.top_percent || 0) * 100}%`,
                      width: `${(r.width_percent || 0) * 100}%`,
                      height: `${(r.height_percent || 0) * 100}%`,
                      border: `2px solid ${color}`,
                      backgroundColor: `${color}22`,
                      pointerEvents: 'none',
                    }}
                  >
                    <span style={{
                      position: 'absolute', top: 2, left: 4, fontSize: 11, fontWeight: 600,
                      color: '#fff', backgroundColor: color, padding: '1px 6px', borderRadius: 3,
                    }}>{r.region_label || r.region_name}</span>
                    <div style={{
                      position: 'absolute', bottom: -4, right: -4, width: 10, height: 10,
                      backgroundColor: color, cursor: 'se-resize', pointerEvents: 'auto',
                    }} />
                  </div>
                );
              })}
            </div>
          ) : (
            <div style={{ backgroundColor: '#f8f9fa', border: '2px dashed #ced4da', borderRadius: 8, padding: '60px 24px', textAlign: 'center', color: '#6c757d' }}>
              <p style={{ fontSize: 16, marginBottom: 8 }}>Upload a sample PDF to draw regions</p>
              <p style={{ fontSize: 13 }}>Click "Upload PDF for Preview" to load a drawing</p>
            </div>
          )}
        </div>

        {/* Region list sidebar */}
        <div style={{ width: 300, flexShrink: 0 }}>
          <h4 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12, color: '#495057' }}>
            Regions ({regions.length})
          </h4>
          {regions.length === 0 && (
            <p style={{ fontSize: 13, color: '#6c757d' }}>
              {pdfImage ? 'Draw a rectangle on the PDF to create a region.' : 'Upload a PDF first, then draw regions.'}
            </p>
          )}
          {regions.map((r, i) => {
            const color = REGION_COLORS[i % REGION_COLORS.length];
            const isSelected = selectedRegion === i;
            return (
              <div
                key={i}
                style={{
                  border: `2px solid ${isSelected ? color : '#e9ecef'}`,
                  borderRadius: 6, padding: 10, marginBottom: 8,
                  backgroundColor: isSelected ? `${color}08` : '#fff',
                }}
              >
                <div
                  onClick={() => setSelectedRegion(isSelected ? null : i)}
                  style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: isSelected ? 8 : 0, cursor: 'pointer' }}
                >
                  <span style={{ fontSize: 13, fontWeight: 500, color }}>
                    {r.region_label || r.region_name}
                  </span>
                  {!readOnly && (
                    <button type="button" onClick={(e) => { e.stopPropagation(); handleRemoveRegion(i); }}
                      style={{ background: 'none', border: 'none', color: '#dc3545', cursor: 'pointer', fontSize: 16, padding: 0, lineHeight: 1 }}
                    >&times;</button>
                  )}
                </div>
                {isSelected && (
                  <div style={{ fontSize: 13 }} onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
                    <div style={{ marginBottom: 6 }}>
                      <label style={smLabel}>Label</label>
                      <input value={r.region_label || ''} disabled={readOnly} onChange={e => handleRegionPropChange(i, 'region_label', e.target.value)} style={smInput} />
                    </div>
                    <div style={{ marginBottom: 6 }}>
                      <label style={smLabel}>Name (key)</label>
                      <input value={r.region_name || ''} disabled={readOnly} onChange={e => handleRegionPropChange(i, 'region_name', e.target.value)} style={smInput} />
                    </div>
                    <div style={{ marginBottom: 6 }}>
                      <label style={smLabel}>Type</label>
                      <select value={r.type || 'table'} disabled={readOnly} onChange={e => handleRegionPropChange(i, 'type', e.target.value)} style={smInput}>
                        {REGION_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                      </select>
                    </div>
                    <div style={{ fontSize: 11, color: '#6c757d', marginTop: 4 }}>
                      L:{(r.left_percent || 0).toFixed(3)} T:{(r.top_percent || 0).toFixed(3)} W:{(r.width_percent || 0).toFixed(3)} H:{(r.height_percent || 0).toFixed(3)}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const btnPrimary = { padding: '8px 16px', backgroundColor: '#0d6efd', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 500, cursor: 'pointer' };
const btnOutline = { padding: '8px 16px', backgroundColor: '#fff', color: '#0d6efd', border: '1px solid #0d6efd', borderRadius: 6, fontSize: 13, fontWeight: 500, cursor: 'pointer' };
const smLabel = { display: 'block', marginBottom: 2, fontSize: 11, fontWeight: 500, color: '#6c757d' };
const smInput = { width: '100%', padding: '4px 8px', border: '1px solid #ced4da', borderRadius: 3, fontSize: 13, fontFamily: 'inherit' };

export default RegionEditor;
